import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, MenuItem, Snackbar, Stack, TextField, ToggleButton, ToggleButtonGroup,
  Tooltip, Typography,
} from '@mui/material';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import DensitySmallIcon from '@mui/icons-material/DensitySmall';
import type { BoardCard, Stage } from '../api/types';
import { useBoard, useMoveStage, usePartners, useUsers } from '../api/hooks';
import { KanbanBoard } from '../components/board/KanbanBoard';
import { HistoricalSection } from '../components/board/HistoricalSection';
import type { Density } from '../components/board/OpportunityCard';
import { CloseOpportunityDialog, CloseResult } from '../components/board/CloseOpportunityDialog';
import { ErrorState, LoadingState, useErrorMessage } from '../components/common/Common';
import { NewOpportunityFlow } from '../components/forms/NewOpportunityFlow';

const DENSITY_KEY = 'crm-board-density';

/** Compact by default (ADR-72): the request was to fit more opportunities on screen, so that has
 *  to be what everyone gets without hunting for a setting. */
function readStoredDensity(): Density {
  return localStorage.getItem(DENSITY_KEY) === 'comfortable' ? 'comfortable' : 'compact';
}

export function BoardPage() {
  const { t } = useTranslation();
  const describeError = useErrorMessage();

  const [density, setDensity] = useState<Density>(readStoredDensity);
  const [ownerUserId, setOwnerUserId] = useState('');
  const [partnerId, setPartnerId] = useState('');

  const { data, isLoading, error, refetch } = useBoard({
    ownerUserId: ownerUserId || undefined,
    partnerId: partnerId ? Number(partnerId) : undefined,
  });
  const { data: users } = useUsers();
  const { data: partners } = usePartners();
  const moveStage = useMoveStage();

  const [pendingClose, setPendingClose] = useState<BoardCard | null>(null);
  const [newOppOpen, setNewOppOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem(DENSITY_KEY, density);
  }, [density]);

  const move = (card: BoardCard, _from: Stage, to: Stage) => {
    // Closing needs an outcome (and a reason if lost), so intercept before moving.
    if (to === 'closed') {
      setPendingClose(card);
      return;
    }
    moveStage.mutate(
      { id: card.id, move: { toStage: to, closedOutcome: null, lostReason: null } },
      { onError: (e) => setToast(describeError(e)) },
    );
  };

  const confirmClose = (result: CloseResult) => {
    const card = pendingClose;
    setPendingClose(null);
    if (!card) return;
    moveStage.mutate(
      { id: card.id, move: { toStage: 'closed', closedOutcome: result.outcome, lostReason: result.lostReason } },
      { onError: (e) => setToast(describeError(e)) },
    );
  };

  if (isLoading) return <LoadingState label={t('board.loading')} />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!data) return null;

  const totalCards = data.columns.reduce((n, c) => n + c.cards.length, 0);

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 2.5 }}
      >
        <Stack direction="row" alignItems="baseline" spacing={1.5}>
          <Typography variant="h1">{t('board.title')}</Typography>
          <Typography variant="body2" color="text.secondary" data-testid="board-count">
            {t('board.opportunityCount', { count: totalCards })}
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <TextField
            select
            size="small"
            label={t('board.filters.owner')}
            value={ownerUserId}
            onChange={(e) => setOwnerUserId(e.target.value)}
            sx={{ minWidth: 160 }}
            data-testid="board-filter-owner"
            // Without displayEmpty the "any owner" option renders as an empty box, because MUI
            // treats the empty string as "nothing selected".
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">{t('board.filters.anyOwner')}</MenuItem>
            {(users ?? []).map((u) => <MenuItem key={u.userId} value={u.userId}>{u.name}</MenuItem>)}
          </TextField>

          <TextField
            select
            size="small"
            label={t('board.filters.partner')}
            value={partnerId}
            onChange={(e) => setPartnerId(e.target.value)}
            sx={{ minWidth: 160 }}
            data-testid="board-filter-partner"
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">{t('board.filters.anyPartner')}</MenuItem>
            {(partners?.items ?? []).map((p) => (
              <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>
            ))}
          </TextField>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={density}
            onChange={(_, value: Density | null) => value && setDensity(value)}
            data-testid="board-density-toggle"
            aria-label={t('board.density.label')}
          >
            <ToggleButton value="compact" data-testid="density-compact">
              <Tooltip title={t('board.density.compact')}><DensitySmallIcon fontSize="small" /></Tooltip>
            </ToggleButton>
            <ToggleButton value="comfortable" data-testid="density-comfortable">
              <Tooltip title={t('board.density.comfortable')}><ViewAgendaIcon fontSize="small" /></Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      <KanbanBoard
        board={data}
        density={density}
        onMove={move}
        onNewOpportunity={() => setNewOppOpen(true)}
        historical={<HistoricalSection />}
      />

      <CloseOpportunityDialog
        open={Boolean(pendingClose)}
        title={pendingClose?.title ?? ''}
        onConfirm={confirmClose}
        onCancel={() => setPendingClose(null)}
      />

      <NewOpportunityFlow open={newOppOpen} onClose={() => setNewOppOpen(false)} />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
}
